import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import {
  customerInvestmentsSearchSchema,
  useCustomerInvestments,
} from '@/hooks/queries/crt/useCustomerInvestments'
import { useTablePagination } from '@/hooks/useTablePagination'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AssumptionSelection,
  toAssumptionBadges,
  toSelectionSearch,
} from '../../-components/assumptions/assumption-selection'
import { ConfigureAssumptions } from '../../-components/assumptions/ConfigureAssumptions'
import { InvestmentsDataTable } from './-components/InvestmentsDataTable'

const ASSUMPTION_KINDS = ['manual'] as const

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/investments/')({
  component: RouteComponent,
  validateSearch: (search) => customerInvestmentsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: ['manualInvestmentIds'],
  })
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configuration = { manualInvestmentIds: search.manualInvestmentIds }
  const configBadges = toAssumptionBadges(t, ASSUMPTION_KINDS, configuration)
  const handleApplyConfig = (selection: AssumptionSelection) => {
    navigate({ search: { ...search, ...toSelectionSearch(ASSUMPTION_KINDS, selection), page: 1 } })
  }

  const { loadedVersionId: _loadedVersionId, ...dataSearch } = search
  const queries = {
    ...dataSearch,
    crtPeriod,
    page: search.page ?? 1,
    pageSize,
  }

  const { isPending, error, data } = useCustomerInvestments(queries)

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('investments.pageHeader')}</PageHeader>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <InvestmentsDataTable
            data={data?.results ?? []}
            rowCount={data?.rowCount ?? 0}
            totals={data?.totals}
            isPending={isPending}
            error={error}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            toolbarActions={
              <ConfigureAssumptions
                isOpen={isConfigOpen}
                onOpenChange={setIsConfigOpen}
                crtPeriod={crtPeriod}
                kinds={ASSUMPTION_KINDS}
                selection={configuration}
                onApply={handleApplyConfig}
              />
            }
            configBadges={configBadges}
            onConfigBadgeClick={() => setIsConfigOpen(true)}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
